// API Client for Finance App Backend

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
const TOKEN_KEY = 'finance_app_token';
const BULK_TRANSACTION_CHUNK_SIZE = 200;

class APIError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.data = data;
  }
}

// Helper to get auth headers (now reads token from localStorage)
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY);
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// Generic fetch wrapper with error handling
async function fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message =
      typeof errorData === 'object' && errorData !== null && 'error' in errorData && typeof errorData.error === 'string'
        ? errorData.error
        : `API Error: ${response.statusText}`;

    // Auto-logout on 401 — token is invalid or expired
    if (response.status === 401 && !endpoint.startsWith('/auth/')) {
      localStorage.removeItem(TOKEN_KEY);
      window.location.href = '/login';
    }

    throw new APIError(message, response.status, errorData);
  }

  return response.json();
}

type BulkTransactionPayload = {
  accountId: string;
  categoryId?: string;
  occurredAt: string;
  description: string;
  merchant?: string;
  notes?: string;
  amountCents: number;
  currency?: string;
  allocations?: Array<{ jarId: string; amountCents: number }>;
};

function chunkArray<T>(items: T[], chunkSize: number): T[][] {
  if (items.length === 0) {
    return [];
  }

  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
}

async function createTransactionsBulkChunk(
  transactions: BulkTransactionPayload[]
): Promise<{ transactions: any[]; count: number }> {
  try {
    return await fetchAPI<{ transactions: any[]; count: number }>('/transactions/bulk', {
      method: 'POST',
      body: JSON.stringify(transactions),
    });
  } catch (error) {
    if (error instanceof APIError && error.status === 413 && transactions.length > 1) {
      const midpoint = Math.ceil(transactions.length / 2);
      const left = await createTransactionsBulkChunk(transactions.slice(0, midpoint));
      const right = await createTransactionsBulkChunk(transactions.slice(midpoint));

      return {
        transactions: [...left.transactions, ...right.transactions],
        count: left.count + right.count,
      };
    }

    throw error;
  }
}

// Types matching the API responses

export interface ApiAccountTag {
  id: string;
  name: string;
  color: string;
  createdAt?: string;
}

export interface ApiAccountMember {
  userId: string;
  email: string | null;
  name: string | null;
  role: string;
  createdAt: string;
}

export interface ApiAccount {
  id: string;
  userId: string;
  name: string;
  type: 'CHECKING' | 'SAVINGS' | 'CREDIT' | 'CASH' | 'INVESTMENT' | 'REAL_ESTATE' | 'VEHICLE' | 'LOAN' | 'OTHER';
  behavior: 'cash' | 'asset' | 'investment' | 'credit' | 'liability';
  classification: string | null;
  institution: string | null;
  currency: string;
  balanceCents: number;
  originalBalanceCents: number | null;
  notes: string | null;
  status: 'active' | 'archived' | 'closed';
  closedAt: string | null;
  isLinked: boolean;
  externalId: string | null;
  lastSyncedAt: string | null;
  tags: ApiAccountTag[];
  createdAt: string;
  updatedAt: string;
}

export interface ApiCategory {
  id: string;
  userId: string;
  name: string;
  parentId?: string | null;
  kind?: 'income' | 'expense';
  billingIntervalMonths?: number;
  sortOrder?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiTransaction {
  id: string;
  userId: string;
  occurredAt: string;
  description: string;
  merchant: string | null;
  notes: string | null;
  amountCents: number;
  currency: string;
  isTransfer: boolean;
  createdAt: string;
  updatedAt: string;
  account: {
    id: string;
    name: string;
    type: string;
  };
  category: {
    id: string;
    name: string;
  } | null;
  allocations: Array<{
    jar: {
      id: string;
      name: string;
    };
    amountCents: number;
  }>;
}

export interface ApiTransactionRule {
  id: string;
  userId: string;
  field: 'description' | 'account' | 'category';
  operator: 'contains' | 'equals';
  value: string;
  caseSensitive: boolean;
  categoryId: string;
  categoryName: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiBudgetCategoryNode {
  id: string;
  name: string;
  kind: 'income' | 'expense';
  billingIntervalMonths: number;
  sortOrder: number;
  budgeted: number;
  actual: number;
  average3Months: number;
  subcategories: ApiBudgetCategoryNode[];
}

export interface ApiBudgetPeriod {
  id: string;
  name: string;
  year: number;
  month: number;
  startDate: string;
  endDate: string;
}

export interface ApiRulePreviewRow {
  id: string;
  occurredAt: string;
  description: string;
  amountCents: number;
  currency: string;
  accountName: string;
  categoryName: string | null;
}

export interface ApiJar {
  id: string;
  name: string;
  description: string | null;
  ownerId: string;
  targetAmountCents: number;
  currentAmountCents: number;
  targetDate: string | null;
  currency: string;
  icon: string;
  color: string;
  expenseCount: number;
  totalExpenseCents: number;
  createdAt: string;
  updatedAt: string;
  owner: {
    id: string;
    email: string | null;
    name: string | null;
  };
  members: Array<{
    jarId: string;
    userId: string;
    role: string;
    user: {
      id: string;
      email: string | null;
      name: string | null;
    };
  }>;
}

export interface ApiJarExpense {
  id: string;
  jarId: string;
  transactionId: string | null;
  categoryId: string | null;
  description: string;
  amountCents: number;
  currency: string;
  occurredAt: string;
  paidByUserId: string;
  paidByName: string;
  icon: string;
  iconBg: string;
  splitType: string;
  createdAt: string;
  updatedAt: string;
  splits: Array<{
    id: string;
    userId: string;
    amountCents: number;
    name?: string;
    email?: string;
  }>;
}

export interface ApiJarContribution {
  id: string;
  jarId: string;
  userId: string;
  amountCents: number;
  currency: string;
  note: string | null;
  accountId: string | null;
  accountName: string | null;
  occurredAt: string;
  userName: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiJarCategory {
  id: string;
  jarId: string;
  name: string;
  icon: string;
  color: string;
  sortOrder: number;
  createdAt: string;
}

export interface ApiJarPlanningItem {
  id: string;
  jarId: string;
  parentId: string | null;
  name: string;
  amountCents: number;
  status: string;
  sortOrder: number;
  children: ApiJarPlanningItem[];
}

export interface ApiJarSettlement {
  balances: Array<{ userId: string; name: string; balanceCents: number }>;
  contributions: Array<{ userId: string; name: string; totalCents: number }>;
  settlements: Array<{ from: string; fromName: string; to: string; toName: string; amountCents: number }>;
}

export interface ApiJarInvite {
  id: string;
  jarId: string;
  jarName: string;
  invitedUserId: string | null;
  invitedUserName: string | null;
  invitedEmail: string;
  invitedByUserId: string;
  invitedByName: string | null;
  invitedByEmail: string | null;
  role: 'VIEWER' | 'EDITOR';
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED';
  createdAt: string;
  updatedAt: string;
  respondedAt: string | null;
}

export interface ApiJarActivity {
  id: string;
  type: 'jar_created' | 'contribution' | 'expense' | 'invite_sent' | 'invite_accepted' | 'invite_declined';
  jarId: string;
  jarName: string;
  actorUserId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  subjectName: string | null;
  subjectEmail: string | null;
  amountCents: number | null;
  description: string;
  createdAt: string;
}

// API Functions

export const api = {
  // Health check
  health: () => fetchAPI<{ status: string; timestamp: string; database: string }>('/health'),

  // Accounts
  getAccounts: (status?: 'active' | 'archived' | 'closed' | 'all') => {
    const qs = status ? `?status=${status}` : '';
    return fetchAPI<{ accounts: ApiAccount[] }>(`/accounts${qs}`);
  },
  createAccount: (data: {
    name: string;
    behavior: string;
    type?: string;
    classification?: string;
    institution?: string;
    currency?: string;
    balanceCents?: number;
    originalBalanceCents?: number;
    notes?: string;
    isLinked?: boolean;
    externalId?: string;
  }) => fetchAPI<{ account: ApiAccount }>('/accounts', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateAccount: (id: string, patch: {
    name?: string;
    behavior?: string;
    classification?: string | null;
    institution?: string | null;
    currency?: string;
    balanceCents?: number;
    originalBalanceCents?: number | null;
    notes?: string | null;
    status?: 'active' | 'archived' | 'closed';
    isLinked?: boolean;
    externalId?: string | null;
  }) => fetchAPI<{ account: ApiAccount }>(`/accounts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  }),
  deleteAccount: (id: string, force?: boolean) => {
    const qs = force ? '?force=true' : '';
    return fetchAPI<{ ok: true; action: string; closedAt?: string }>(`/accounts/${id}${qs}`, {
      method: 'DELETE',
    });
  },
  restoreAccount: (id: string) =>
    fetchAPI<{ ok: true }>(`/accounts/${id}/restore`, { method: 'POST' }),

  // Account tags
  getAccountTags: () => fetchAPI<{ tags: ApiAccountTag[] }>('/account-tags'),
  createAccountTag: (data: { name: string; color?: string }) =>
    fetchAPI<{ tag: ApiAccountTag }>('/account-tags', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  deleteAccountTag: (id: string) =>
    fetchAPI<{ ok: true }>(`/account-tags/${id}`, { method: 'DELETE' }),
  assignAccountTag: (accountId: string, tagId: string) =>
    fetchAPI<{ ok: true }>(`/accounts/${accountId}/tags`, {
      method: 'POST',
      body: JSON.stringify({ tagId }),
    }),
  removeAccountTag: (accountId: string, tagId: string) =>
    fetchAPI<{ ok: true }>(`/accounts/${accountId}/tags/${tagId}`, { method: 'DELETE' }),

  // Account members (shared ownership)
  getAccountMembers: (accountId: string) =>
    fetchAPI<{ members: ApiAccountMember[] }>(`/accounts/${accountId}/members`),
  addAccountMember: (accountId: string, data: { email: string; role?: string }) =>
    fetchAPI<{ member: ApiAccountMember }>(`/accounts/${accountId}/members`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  removeAccountMember: (accountId: string, memberId: string) =>
    fetchAPI<{ ok: true }>(`/accounts/${accountId}/members/${memberId}`, { method: 'DELETE' }),

  // Account grouping / aggregation
  getAccountsGrouped: (groupBy?: string) => {
    const qs = groupBy ? `?groupBy=${groupBy}` : '';
    return fetchAPI<{
      groupBy: string;
      groups: Array<{
        key: string;
        count: number;
        totalBalanceCents: number;
        assetsCents: number;
        liabilitiesCents: number;
      }>;
    }>(`/accounts/grouped${qs}`);
  },

  // Net Worth
  getNetWorth: (currency?: string) => {
    const qs = currency ? `?currency=${currency}` : '';
    return fetchAPI<{
      totalAssetsCents: number;
      totalLiabilitiesCents: number;
      netWorthCents: number;
      byCurrency: Array<{
        currency: string;
        totalAssetsCents: number;
        totalLiabilitiesCents: number;
        netWorthCents: number;
        accountCount: number;
      }>;
    }>(`/net-worth${qs}`);
  },
  getNetWorthHistory: (months?: number) => {
    const qs = months ? `?months=${months}` : '';
    return fetchAPI<{
      snapshots: Array<{
        date: string;
        totalAssetsCents: number;
        totalLiabilitiesCents: number;
        netWorthCents: number;
      }>;
    }>(`/net-worth/history${qs}`);
  },

  // Categories
  getCategories: () => fetchAPI<{ categories: ApiCategory[] }>('/categories'),
  createCategory: (data: {
    name: string;
    parentId?: string | null;
    kind?: 'income' | 'expense';
    billingIntervalMonths?: number;
    sortOrder?: number;
  }) =>
    fetchAPI<{ category: ApiCategory }>('/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  previewCategoryUsage: (id: string) =>
    fetchAPI<{
      transactions: number;
      rules: number;
      allocations: number;
      childCategories: number;
    }>(`/categories/${id}/usage`),

  forceDeleteCategory: (id: string, options: { unassignTransactions?: boolean; deleteRules?: boolean; deleteAllocations?: boolean }) =>
    fetchAPI<{ ok: true }>(`/categories/${id}/force-delete`, {
      method: 'POST',
      body: JSON.stringify(options),
    }),

  updateCategory: (
    id: string,
    patch: {
      name?: string;
      parentId?: string | null;
      kind?: 'income' | 'expense';
      billingIntervalMonths?: number;
      sortOrder?: number;
    }
  ) =>
    fetchAPI<{ category: ApiCategory }>(`/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteCategory: (id: string) =>
    fetchAPI<{ ok: true }>(`/categories/${id}`, {
      method: 'DELETE',
    }),

  // Budget
  getBudget: (params?: { year?: number; month?: number }) => {
    const qs = new URLSearchParams();
    if (params?.year) qs.set('year', String(params.year));
    if (params?.month) qs.set('month', String(params.month));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return fetchAPI<{ period: ApiBudgetPeriod; categories: ApiBudgetCategoryNode[] }>(`/budget${suffix}`);
  },

  saveBudgetAllocations: (periodId: string, allocations: Array<{ categoryId: string; allocatedCents: number }>) =>
    fetchAPI<{ ok: true; count: number }>(`/budget/${periodId}/allocations`, {
      method: 'PUT',
      body: JSON.stringify({ allocations }),
    }),

  // Transactions
  getTransactions: (params?: {
    limit?: number;
    offset?: number;
    sort?: 'date' | 'description' | 'category' | 'account' | 'amount';
    direction?: 'asc' | 'desc';
    search?: string;
    categoryIds?: string[];
    accounts?: string[];
    minAmount?: number;
    maxAmount?: number;
    startDate?: string;
    endDate?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    if (params?.sort) qs.set('sort', params.sort);
    if (params?.direction) qs.set('direction', params.direction);
    if (params?.search) qs.set('search', params.search);
    if (params?.categoryIds?.length) qs.set('categoryIds', params.categoryIds.join(','));
    if (params?.accounts?.length) qs.set('accounts', params.accounts.join(','));
    if (params?.minAmount != null) qs.set('minAmount', String(params.minAmount));
    if (params?.maxAmount != null) qs.set('maxAmount', String(params.maxAmount));
    if (params?.startDate) qs.set('startDate', params.startDate);
    if (params?.endDate) qs.set('endDate', params.endDate);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return fetchAPI<{ transactions: ApiTransaction[]; total: number; limit: number; offset: number }>(`/transactions${suffix}`);
  },
  createTransaction: (data: {
    accountId: string;
    categoryId?: string;
    occurredAt: string;
    description: string;
    merchant?: string;
    notes?: string;
    amountCents: number;
    currency?: string;
    allocations?: Array<{ jarId: string; amountCents: number }>;
  }) =>
    fetchAPI<{ transaction: ApiTransaction }>('/transactions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  createTransactionsBulk: async (transactions: BulkTransactionPayload[]) => {
    const chunks = chunkArray(transactions, BULK_TRANSACTION_CHUNK_SIZE);
    const createdTransactions: any[] = [];

    for (const chunk of chunks) {
      const response = await createTransactionsBulkChunk(chunk);
      createdTransactions.push(...response.transactions);
    }

    return {
      transactions: createdTransactions,
      count: createdTransactions.length,
    };
  },

  updateTransaction: (id: string, patch: {
    description?: string;
    merchant?: string | null;
    notes?: string | null;
    amountCents?: number;
    occurredAt?: string;
    accountId?: string;
    categoryId?: string | null;
    currency?: string;
  }) =>
    fetchAPI<{ transaction: ApiTransaction }>(`/transactions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  updateTransactionCategory: (id: string, categoryId: string | null) =>
    fetchAPI<{ ok: true; transactionId: string; categoryId: string | null }>(`/transactions/${id}/category`, {
      method: 'PATCH',
      body: JSON.stringify({ categoryId }),
    }),

  // Transaction rules
  getTransactionRules: () => fetchAPI<{ rules: ApiTransactionRule[] }>('/transaction-rules'),
  createTransactionRule: (data: {
    field: 'description' | 'account' | 'category';
    operator: 'contains' | 'equals';
    value: string;
    caseSensitive?: boolean;
    categoryId: string;
  }) =>
    fetchAPI<{ rule: ApiTransactionRule }>('/transaction-rules', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateTransactionRule: (id: string, data: {
    field?: 'description' | 'account' | 'category';
    operator?: 'contains' | 'equals';
    value?: string;
    caseSensitive?: boolean;
    categoryId?: string;
  }) =>
    fetchAPI<{ rule: ApiTransactionRule }>(`/transaction-rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteTransactionRule: (id: string) =>
    fetchAPI<{ ok: true }>(`/transaction-rules/${id}`, {
      method: 'DELETE',
    }),

  previewTransactionRule: (data: {
    field: 'description' | 'account' | 'category';
    operator: 'contains' | 'equals';
    value: string;
    caseSensitive?: boolean;
    categoryId: string;
    onlyUncategorized?: boolean;
    limit?: number;
  }) =>
    fetchAPI<{ count: number; sample: ApiRulePreviewRow[] }>('/transaction-rules/preview', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  applyTransactionRule: (data: {
    field: 'description' | 'account' | 'category';
    operator: 'contains' | 'equals';
    value: string;
    caseSensitive?: boolean;
    categoryId: string;
    onlyUncategorized?: boolean;
  }) =>
    fetchAPI<{ ok: true; updatedCount: number }>('/transaction-rules/apply', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Bulk transaction operations
  deleteTransaction: (id: string) =>
    fetchAPI<{ ok: true }>(`/transactions/${id}`, { method: 'DELETE' }),

  bulkDeleteTransactions: (ids: string[]) =>
    fetchAPI<{ ok: true; deleted: number }>('/transactions/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  bulkSetCategory: (ids: string[], categoryId: string | null) =>
    fetchAPI<{ ok: true; updated: number }>('/transactions/bulk-category', {
      method: 'PATCH',
      body: JSON.stringify({ ids, categoryId }),
    }),

  bulkAssignJar: (ids: string[], jarId: string) =>
    fetchAPI<{ ok: true; assigned: number }>('/transactions/bulk-jar', {
      method: 'POST',
      body: JSON.stringify({ ids, jarId }),
    }),

  exportTransactionsCsv: async (params?: { startDate?: string; endDate?: string }) => {
    const qs = new URLSearchParams();
    if (params?.startDate) qs.set('startDate', params.startDate);
    if (params?.endDate) qs.set('endDate', params.endDate);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const url = `${API_BASE_URL}/transactions/export${suffix}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'transactions.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  },

  createTransfer: (data: {
    fromAccountId: string;
    toAccountId: string;
    amountCents: number;
    occurredAt: string;
    description?: string;
    currency?: string;
    notes?: string;
  }) =>
    fetchAPI<{
      transfer: {
        transferId: string;
        outflow: { id: string; accountId: string; amountCents: number };
        inflow: { id: string; accountId: string; amountCents: number };
        occurredAt: string;
        description: string;
        currency: string;
      };
    }>('/transactions/transfer', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Jars
  getJars: () => fetchAPI<{ jars: ApiJar[] }>('/jars'),
  createJar: (data: {
    name: string;
    description?: string;
    targetAmountCents?: number;
    currentAmountCents?: number;
    targetDate?: string;
    currency?: string;
    icon?: string;
    color?: string;
  }) =>
    fetchAPI<{ jar: ApiJar }>('/jars', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateJar: (jarId: string, patch: {
    name?: string;
    description?: string | null;
    targetAmountCents?: number;
    currentAmountCents?: number;
    targetDate?: string | null;
    currency?: string;
    icon?: string;
    color?: string;
  }) =>
    fetchAPI<{ jar: ApiJar }>(`/jars/${jarId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteJar: (jarId: string) =>
    fetchAPI<{ ok: true }>(`/jars/${jarId}`, {
      method: 'DELETE',
    }),
  addJarMember: (jarId: string, data: { userId?: string; email?: string; role?: string }) =>
    fetchAPI<{ member: any }>(`/jars/${jarId}/members`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getJarInvites: () =>
    fetchAPI<{ invites: ApiJarInvite[] }>('/jars/invites'),
  createJarInvite: (jarId: string, data: { email: string; role?: 'VIEWER' | 'EDITOR' }) =>
    fetchAPI<{ invite: ApiJarInvite }>(`/jars/${jarId}/invites`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  respondToJarInvite: (inviteId: string, action: 'accept' | 'decline') =>
    fetchAPI<{ ok: true; invite: ApiJarInvite }>(`/jars/invites/${inviteId}/respond`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),
  getJarActivity: (limit?: number) => {
    const qs = limit ? `?limit=${limit}` : '';
    return fetchAPI<{ activity: ApiJarActivity[] }>(`/jars/activity${qs}`);
  },
  removeJarMember: (jarId: string, memberId: string) =>
    fetchAPI<{ ok: true }>(`/jars/${jarId}/members/${memberId}`, {
      method: 'DELETE',
    }),

  // Jar expenses (Splitwise-style)
  getJarExpenses: (jarId: string) =>
    fetchAPI<{ expenses: ApiJarExpense[] }>(`/jars/${jarId}/expenses`),

  createJarExpense: (jarId: string, data: {
    description: string;
    amountCents: number;
    currency?: string;
    occurredAt: string;
    paidByUserId: string;
    transactionId?: string;
    categoryId?: string;
    icon?: string;
    iconBg?: string;
    splitType?: string;
    splits: Array<{ userId: string; amountCents: number }>;
  }) =>
    fetchAPI<{ expense: ApiJarExpense; jarBalance: number }>(`/jars/${jarId}/expenses`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateJarExpense: (jarId: string, expenseId: string, data: {
    description?: string;
    amountCents?: number;
    occurredAt?: string;
    categoryId?: string | null;
    splitType?: string;
    splits?: Array<{ userId: string; amountCents: number }>;
    paidByUserId?: string;
  }) =>
    fetchAPI<{ expense: ApiJarExpense }>(`/jars/${jarId}/expenses/${expenseId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteJarExpense: (jarId: string, expenseId: string) =>
    fetchAPI<{ ok: true; jarBalance: number }>(`/jars/${jarId}/expenses/${expenseId}`, {
      method: 'DELETE',
    }),

  // Jar Contributions
  getJarContributions: (jarId: string) =>
    fetchAPI<{ contributions: ApiJarContribution[] }>(`/jars/${jarId}/contributions`),

  createJarContribution: (jarId: string, data: {
    amountCents: number;
    currency?: string;
    note?: string;
    occurredAt: string;
    accountId?: string;
  }) =>
    fetchAPI<{ contribution: ApiJarContribution; jarBalance: number }>(`/jars/${jarId}/contributions`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateJarContribution: (jarId: string, contributionId: string, data: {
    amountCents?: number;
    note?: string | null;
    occurredAt?: string;
    accountId?: string | null;
  }) =>
    fetchAPI<{ ok: true; jarBalance: number }>(`/jars/${jarId}/contributions/${contributionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteJarContribution: (jarId: string, contributionId: string) =>
    fetchAPI<{ ok: true; jarBalance: number }>(`/jars/${jarId}/contributions/${contributionId}`, {
      method: 'DELETE',
    }),

  // Jar Planning Items
  getJarPlanningItems: (jarId: string) =>
    fetchAPI<{ items: ApiJarPlanningItem[] }>(`/jars/${jarId}/planning-items`),

  createJarPlanningItem: (jarId: string, data: {
    parentId?: string | null;
    name: string;
    amountCents?: number;
    status?: string;
    sortOrder?: number;
  }) =>
    fetchAPI<{ item: ApiJarPlanningItem }>(`/jars/${jarId}/planning-items`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateJarPlanningItem: (jarId: string, itemId: string, data: {
    name?: string;
    amountCents?: number;
    status?: string;
    sortOrder?: number;
  }) =>
    fetchAPI<{ ok: true }>(`/jars/${jarId}/planning-items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteJarPlanningItem: (jarId: string, itemId: string) =>
    fetchAPI<{ ok: true }>(`/jars/${jarId}/planning-items/${itemId}`, {
      method: 'DELETE',
    }),

  // Jar Categories (shared categories within a jar)
  getJarCategories: (jarId: string) =>
    fetchAPI<{ categories: ApiJarCategory[] }>(`/jars/${jarId}/categories`),

  createJarCategory: (jarId: string, data: { name: string; icon?: string; color?: string }) =>
    fetchAPI<{ category: ApiJarCategory }>(`/jars/${jarId}/categories`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteJarCategory: (jarId: string, categoryId: string) =>
    fetchAPI<{ ok: true }>(`/jars/${jarId}/categories/${categoryId}`, {
      method: 'DELETE',
    }),

  // Bulk expense split update
  bulkSplitJarExpenses: (jarId: string, data: {
    expenseIds: string[];
    splitType: string;
    perUser?: Record<string, number>;
  }) =>
    fetchAPI<{ ok: true; updatedCount: number }>(`/jars/${jarId}/expenses/bulk-split`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Auto-categorize jar expenses using user's transaction rules
  autoCategorizeJarExpenses: (jarId: string) =>
    fetchAPI<{ ok: true; categorized: number; total: number }>(`/jars/${jarId}/expenses/auto-categorize`, {
      method: 'POST',
    }),

  // Jar Settlement
  getJarSettlement: (jarId: string) =>
    fetchAPI<ApiJarSettlement>(`/jars/${jarId}/settlement`),

  // ── Reports ─────────────────────────────────────────────────

  getReportMonthlySummary: (months?: number) => {
    const qs = months ? `?months=${months}` : '';
    return fetchAPI<{
      months: Array<{
        month: string;
        incomeCents: number;
        expenseCents: number;
        uncategorizedCents: number;
        transactionCount: number;
      }>;
    }>(`/reports/monthly-summary${qs}`);
  },

  getReportCategorySpending: (months?: number) => {
    const qs = months ? `?months=${months}` : '';
    return fetchAPI<{
      categories: Array<{
        id: string;
        name: string;
        totalCents: number;
        txCount: number;
        subcategories: Array<{
          id: string;
          name: string;
          totalCents: number;
          txCount: number;
        }>;
      }>;
      months: number;
    }>(`/reports/category-spending${qs}`);
  },

  getReportCategoryDrilldown: (categoryId: string, months?: number) => {
    const qs = new URLSearchParams();
    qs.set('categoryId', categoryId);
    if (months) qs.set('months', String(months));
    return fetchAPI<{
      parentId: string;
      parentName: string;
      subcategories: Array<{ id: string; name: string }>;
      series: Array<Record<string, any>>;
      months: number;
    }>(`/reports/category-drilldown?${qs.toString()}`);
  },

  getReportSubcategorySpending: (parentId: string, months?: number) => {
    const qs = new URLSearchParams();
    qs.set('parentId', parentId);
    if (months) qs.set('months', String(months));
    return fetchAPI<{
      parentId: string;
      parentName: string;
      subcategories: Array<{
        id: string;
        name: string;
        totalCents: number;
        txCount: number;
        hasChildren: boolean;
      }>;
      months: number;
    }>(`/reports/subcategory-spending?${qs.toString()}`);
  },

  getReportCategoryTrends: (months?: number, limit?: number) => {
    const qs = new URLSearchParams();
    if (months) qs.set('months', String(months));
    if (limit) qs.set('limit', String(limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return fetchAPI<{
      categories: string[];
      series: Array<Record<string, any>>;
      months: number;
    }>(`/reports/category-trends${suffix}`);
  },

  getReportTopMerchants: (months?: number, limit?: number) => {
    const qs = new URLSearchParams();
    if (months) qs.set('months', String(months));
    if (limit) qs.set('limit', String(limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return fetchAPI<{
      merchants: Array<{
        name: string;
        totalCents: number;
        txCount: number;
      }>;
      months: number;
    }>(`/reports/top-merchants${suffix}`);
  },

  getReportDailyTotals: (months?: number) => {
    const qs = months ? `?months=${months}` : '';
    return fetchAPI<{
      days: Array<{
        day: string;
        incomeCents: number;
        expenseCents: number;
        txCount: number;
      }>;
      months: number;
    }>(`/reports/daily-totals${qs}`);
  },

  // Auth
  changePassword: (currentPassword: string, newPassword: string) =>
    fetchAPI<{ ok: true }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  resetPassword: (email: string, newPassword: string) =>
    fetchAPI<{ ok: true }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email, newPassword }),
    }),
};
